/**
 * HTTP
 * 只能通过本模块接口发起http请求
 */

import axios from 'axios';
import qs from 'qs';
import * as utilCookie from './cookie';
import { TANTU_ORIGIN } from '../config';
import * as utilObject from './object';
import * as logger from './logger';


const CSRF_TOKEN = utilCookie.get('poi_m_web_csrf_token');


/**
 * 拦截器
 */
const interceptors = {
  // 编码请求数据
  enctypeRequestData(config) {
    const newConfig = { ...config };
    if (typeof newConfig.data === 'object') {
      newConfig.data = qs.stringify(newConfig.data);
    }

    return newConfig;
  },

  // 检测 API 网关响应
  checkGatewayResponse(response) {
    // 响应体
    const body = utilObject.get(response, 'data', {});
    const config = utilObject.get(response, 'config');
    const dataSchema = utilObject.get(config, 'dataSchema');

    /**
     * check 检查响应体结构
     * 商定过“响应体结构”
     */
    if (!body || body.code !== 0 || !body.child_status) {
      const error = new ApiProxyError(
        config,
        '服务错误：响应体格式错误',
        utilObject.get(body, 'code', 1),
        utilObject.get(body, 'status', 500)
      );
      throw error;
    }

    /**
     * check 检查网关请求服务层状态
     * - api-server 相应error状态
     * - 只要其中一个 success 就正常返回数据（api-gateway合并多个请求）
     */
    const apiServerErrors = [];
    let valid = false;
    Object.keys(body.child_status).forEach((key) => {
      const res = body.child_status[key];
      if (res.code) {
        body.data[key] = new ApiServerError(
          config,
          res.msg,
          res.code,
          res.api_name,
          res.status
        );

        apiServerErrors.push(body.data[key]);
      } else {
        valid = true;
      }
    });

    if (!valid) {
      const error = new ApiServerErrorGroup(config, apiServerErrors);
      throw error;
    }

    /**
     * check 检查响应数据模式
     */
    if (dataSchema) {
      try {
        dataSchema.validateSync(body.data, { strict: true });
      } catch (error) {
        error.config = config;
        throw error;
      }
    }

    return body.data;
  },

  handleResponseError(error) {
    log(error);
    return Promise.reject(error);
  }
};

/**
 * 面向 m.tantu.com/api/ 接口服务
 */
export const apiTantu = axios.create({
  baseURL: `${TANTU_ORIGIN}/api/`,
  timeout: 15000,
  headers: {
    'x-csrf-token': CSRF_TOKEN,
    'Content-Type': 'application/x-www-form-urlencoded'
  },
  withCredentials: true
});

apiTantu.interceptors.request.use(interceptors.enctypeRequestData);

/**
 * 面向网关代理服务（API Gateway proxy）
 */
export const apiProxy = axios.create({
  baseURL: '/tantu/webapp/api-proxy/',
  timeout: 15000,
  headers: {
    'x-csrf-token': CSRF_TOKEN,
    'Content-Type': 'application/x-www-form-urlencoded'
  }
});

apiProxy.interceptors.request.use(interceptors.enctypeRequestData);
apiProxy.interceptors.response.use(
  interceptors.checkGatewayResponse,
  interceptors.handleResponseError
);


/**
 * 记录日志
 */
function log(error) {
  if (ApiError.isNotLogin(error.code)) {
    return;
  }

  if (utilObject.get(error, 'config.url')) {
    const config = error.config;
    const options = {
      extras: {
        url: config.url,
        method: config.method,
        data: config.data,
        params: config.params,
        headers: config.headers
      }
    };

    error.fingerprint = [error.message, config.url, config.method];
    logger.error(error, options);
  } else {
    if (error.message) {
      error.fingerprint = [error.message];
    }
    logger.error(error);
  }
}

export class ApiError extends Error {
  constructor(message) {
    super(message);
    // fix super 无法赋值
    this.message = message;

    this.errorMessage = message;
    this.name = 'ApiError';
  }

  /**
   * 获取一条 API 错误信息
   * API 错误有时候返回很多信息，但是前端给用户 Toast 一条即可
   * @param message
   * @returns {string|*|string|*}
   */
  static getOneError(message) {
    if (!message) {
      return '';
    }

    if (typeof message === 'string') {
      return message;
    }

    if (Array.isArray(message)) {
      const filterMessage = message.filter(Boolean);
      return ApiError.getOneError(filterMessage[0]);
    }

    const errorKeys = Object.keys(message);

    if (errorKeys.length > 0) {
      return ApiError.getOneError(message[errorKeys[0]]);
    }

    return '';
  }

  static isNotLogin(code) {
    return parseInt(code, 10) === 111;
  }

  // 2504 代表已经生成订单了
  static isCreatedOrder(code) {
    return parseInt(code, 10) === 2504;
  }
}

/**
 * 错误源：
 * 1. API 代理：ApiProxyError
 * 2. API服务：ApiServerError
 * 3. 取消请求：CancelError
 */
class AxiosInterceptorError extends Error {
  constructor(config, message, code = 0, status = 200) {
    super(message);
    // fix super 无法赋值
    this.message = message;
    this.errorMessage = message;
    this.name = 'AxiosInterceptorError';
    this.code = code;
    this.status = status;
    this.config = config;
  }
}

class ApiProxyError extends AxiosInterceptorError {
  constructor(config, message, code, status) {
    super(config, message, code, status);
    this.name = 'ApiProxyError';
  }
}


class ApiServerError extends AxiosInterceptorError {
  constructor(config, message, code, apiName, status) {
    super(config, message, code, status);
    this.apiName = apiName;
    this.name = 'ApiServerError';
    this.message = message;
  }
}

class ApiServerErrorGroup extends AxiosInterceptorError {
  constructor(config, errors, message, code, status) {
    super(config, message || errors[0].message, code || errors[0].code, status);
    this.errors = errors;
  }
}
